import {
  useRef,
  useState,
  ChangeEvent,
  ClipboardEvent,
  DragEvent,
  KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import {
  Mail,
  X,
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  UploadCloud,
  Pencil,
  Check,
  Plus,
  Award,
  GraduationCap,
  Loader2,
  Search,
} from 'lucide-react';

import { useCourses } from '@/hooks/use-courses';
import { CourseDetailModal } from '@/components/initiative/CourseDetailModal';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  sanitizeIdInput,
  validatePalestinianId,
  type IdValidationCode,
} from '@/lib/palestinianId';

const APPLICATIONS_EMAIL = 'initiative@champions.ps';

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 10;

const CREDENTIAL_KINDS = ['license', 'university', 'course', 'other'] as const;
type CredentialKind = (typeof CREDENTIAL_KINDS)[number];

type AttachmentKind = 'cv' | 'license' | 'certificate' | 'id' | 'other';

interface Attachment {
  id: string;
  file: File;
  kind: AttachmentKind;
  label: string;
}

const ID_ERROR_KEYS: Record<IdValidationCode, string> = {
  required: 'initiative.coaches.validation.idRequired',
  nonNumeric: 'initiative.coaches.validation.idNonNumeric',
  tooLong: 'initiative.coaches.validation.idTooLong',
  allSame: 'initiative.coaches.validation.idAllSame',
  blocklisted: 'initiative.coaches.validation.idBlocklisted',
  invalidChecksum: 'initiative.coaches.validation.idChecksum',
};

const KIND_OPTIONS: AttachmentKind[] = ['cv', 'license', 'certificate', 'id', 'other'];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(file: File) {
  const t = file.type;
  const name = file.name.toLowerCase();
  if (t.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic)$/.test(name)) return ImageIcon;
  if (t.includes('sheet') || /\.(xlsx?|csv)$/.test(name)) return FileSpreadsheet;
  return FileText;
}

function inferKind(file: File): AttachmentKind {
  const n = file.name.toLowerCase();
  if (/\b(cv|resume|سيرة)\b/.test(n)) return 'cv';
  if (/\b(license|licence|ترخيص)\b/.test(n)) return 'license';
  if (/\b(id|hawiya|هوية)\b/.test(n)) return 'id';
  if (/\b(certif|cert|شهادة)\b/.test(n)) return 'certificate';
  return 'other';
}

function makeId(): string {
  return `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function CoachApplicationForm() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const required = t('initiative.coaches.validation.required');
  const invalidEmail = t('initiative.coaches.validation.invalidEmail');
  const tooLong = t('initiative.coaches.validation.tooLong');

  const credentialSchema = z.object({
    kind: z.enum(CREDENTIAL_KINDS),
    title: z.string().min(2, t('initiative.coaches.validation.credentialIncomplete')),
    issuer: z.string().min(2, t('initiative.coaches.validation.credentialIncomplete')),
    reference: z.string().optional().or(z.literal('')),
  });

  const schema = z.object({
    fullNameAr: z.string().min(2, required),
    fullNameEn: z.string().min(2, required),
    nationalId: z
      .string()
      .min(1, t('initiative.coaches.validation.idRequired'))
      .superRefine((value, ctx) => {
        const result = validatePalestinianId(value);
        if (!result.ok && result.code) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(ID_ERROR_KEYS[result.code]),
          });
        }
      }),
    credentials: z
      .array(credentialSchema)
      .min(1, t('initiative.coaches.validation.credentialMin')),
    phone: z.string().min(6, required),
    email: z.string().email(invalidEmail),
    location: z.string().min(2, required),
    experience: z.coerce.number().int().min(0).max(60),
    motivation: z.string().min(20, required).max(500, tooLong),
    licenseUrl: z.string().url().optional().or(z.literal('')),
    preferredCourseId: z.string().optional().default(''),
    alternateCourseId: z.string().optional().default(''),
  });

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors, touchedFields },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      credentials: [{ kind: 'license', title: '', issuer: '', reference: '' }],
      preferredCourseId: '',
      alternateCourseId: '',
    },
  });

  const {
    fields: credentialFields,
    append: appendCredential,
    remove: removeCredential,
  } = useFieldArray({ control, name: 'credentials' });

  const idValue = watch('nationalId') ?? '';
  const selectedPrimaryId = (watch('preferredCourseId') as string) ?? '';
  const selectedAlternateId = (watch('alternateCourseId') as string) ?? '';
  const [courseSearch, setCourseSearch] = useState('');
  const [viewingCourseId, setViewingCourseId] = useState<string | null>(null);

  const { data: courses, isLoading: coursesLoading, error: coursesError } = useCourses({
    lang: isRTL ? 'ar' : 'en',
    currency: 'USD',
    limit: 100,
    initiativeOnly: true,
  });

  const filteredCourses = (courses ?? []).filter((c) => {
    if (!courseSearch.trim()) return true;
    const q = courseSearch.toLowerCase();
    return (
      c.title.en?.toLowerCase().includes(q) ||
      c.title.ar?.toLowerCase().includes(q) ||
      c.partnerName.en?.toLowerCase().includes(q) ||
      c.partnerName.ar?.toLowerCase().includes(q) ||
      c.level?.toLowerCase().includes(q)
    );
  });

  type Slot = 'primary' | 'alternate';

  const courseSlot = (id: string): Slot | null => {
    if (id && id === selectedPrimaryId) return 'primary';
    if (id && id === selectedAlternateId) return 'alternate';
    return null;
  };

  const assignSlot = (id: string, slot: Slot) => {
    const currentSlot = courseSlot(id);

    // Clicking the slot the course is already in → unassign.
    if (currentSlot === slot) {
      if (slot === 'primary') setValue('preferredCourseId', '', { shouldDirty: true });
      else setValue('alternateCourseId', '', { shouldDirty: true });
      return;
    }

    // If course is in the other slot, swap by clearing the old slot first.
    if (currentSlot === 'primary') {
      setValue('preferredCourseId', '', { shouldDirty: true });
    } else if (currentSlot === 'alternate') {
      setValue('alternateCourseId', '', { shouldDirty: true });
    }

    if (slot === 'primary') {
      // If primary already had a different course, demote it to alternate (only if alt is empty).
      if (selectedPrimaryId && selectedPrimaryId !== id && !selectedAlternateId) {
        setValue('alternateCourseId', selectedPrimaryId, { shouldDirty: true });
      }
      setValue('preferredCourseId', id, { shouldDirty: true });
    } else {
      // alternate
      if (selectedAlternateId && selectedAlternateId !== id && !selectedPrimaryId) {
        setValue('preferredCourseId', selectedAlternateId, { shouldDirty: true });
      }
      setValue('alternateCourseId', id, { shouldDirty: true });
    }
  };

  const clearSlot = (slot: Slot) => {
    if (slot === 'primary') setValue('preferredCourseId', '', { shouldDirty: true });
    else setValue('alternateCourseId', '', { shouldDirty: true });
  };

  const handleIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = sanitizeIdInput(e.target.value);
    setValue('nationalId', cleaned, { shouldValidate: touchedFields.nationalId === true });
  };

  const handleIdPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    setValue('nationalId', sanitizeIdInput(pasted), { shouldValidate: true });
  };

  const handleIdKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const allowed = [
      'Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter',
    ];
    if (allowed.includes(e.key)) return;
    if (e.key.length === 1 && !/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // ─────── Attachments ───────
  const addFiles = (incoming: FileList | File[]) => {
    setAttachError(null);
    const list = Array.from(incoming);
    if (list.length === 0) return;

    if (attachments.length + list.length > MAX_FILES) {
      setAttachError(
        t('initiative.coaches.validation.tooManyFiles', { max: MAX_FILES })
      );
      return;
    }

    const fresh: Attachment[] = [];
    for (const f of list) {
      if (f.size > MAX_FILE_BYTES) {
        setAttachError(`${f.name}: ${t('initiative.coaches.validation.fileTooLarge')}`);
        return;
      }
      fresh.push({
        id: makeId(),
        file: f,
        kind: inferKind(f),
        label: f.name.replace(/\.[^.]+$/, ''),
      });
    }
    setAttachments((prev) => [...prev, ...fresh]);
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragActive(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    setAttachError(null);
  };

  const updateAttachment = (id: string, patch: Partial<Pick<Attachment, 'kind' | 'label'>>) => {
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  const onSubmit = (values: FormValues) => {
    if (attachments.length === 0) {
      setAttachError(t('initiative.coaches.validation.fileRequired'));
      return;
    }

    setSubmitting(true);
    const idResult = validatePalestinianId(values.nationalId);
    const normalizedId = idResult.normalized;

    const primaryCredential = values.credentials[0];
    const subject = `[Coach Application] ${values.fullNameEn} — ${primaryCredential.title}`;
    const body = [
      `Full name (AR): ${values.fullNameAr}`,
      `Full name (EN): ${values.fullNameEn}`,
      `Palestinian National ID: ${normalizedId}`,
      `Phone (WhatsApp): ${values.phone}`,
      `Email: ${values.email}`,
      `Location in Gaza: ${values.location}`,
      `Years of experience: ${values.experience}`,
      `License document URL: ${values.licenseUrl || '—'}`,
      '',
      `Credentials (${values.credentials.length}):`,
      ...values.credentials.map(
        (c, i) =>
          `${i + 1}. [${c.kind.toUpperCase()}] ${c.title} — ${c.issuer}${c.reference ? ` (${c.reference})` : ''}`
      ),
      '',
      `Attachments to include in this email (${attachments.length}):`,
      ...attachments.map(
        (a, i) =>
          `${i + 1}. [${a.kind.toUpperCase()}] ${a.label} — ${a.file.name} (${formatBytes(a.file.size)})`
      ),
      '',
      'Motivation:',
      values.motivation,
      '',
      'Course preferences (we will enroll the coach in ONE course):',
      ...(values.preferredCourseId
        ? (() => {
            const c = courses?.find((x) => x.id === values.preferredCourseId);
            return [
              c
                ? `  1) Primary  : ${c.title.en} — ${c.partnerName.en} (${c.durationBilingual?.en ?? c.duration})`
                : `  1) Primary  : ${values.preferredCourseId}`,
            ];
          })()
        : ['  1) Primary  : (none selected)']),
      ...(values.alternateCourseId
        ? (() => {
            const c = courses?.find((x) => x.id === values.alternateCourseId);
            return [
              c
                ? `  2) Alternate: ${c.title.en} — ${c.partnerName.en} (${c.durationBilingual?.en ?? c.duration})`
                : `  2) Alternate: ${values.alternateCourseId}`,
            ];
          })()
        : ['  2) Alternate: (none selected)']),
    ].join('\n');

    // TODO: replace mailto with API + multipart upload to a real backend.
    window.location.href = `mailto:${APPLICATIONS_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    toast({
      title: t('initiative.coaches.form.successTitle'),
      description: t('initiative.coaches.form.successBody'),
    });
    reset({
      credentials: [{ kind: 'license', title: '', issuer: '', reference: '' }],
      preferredCourseId: '',
      alternateCourseId: '',
    });
    setCourseSearch('');
    setAttachments([]);
    setAttachError(null);
    setSubmitting(false);
  };

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="grid grid-cols-1 md:grid-cols-2 gap-5"
      dir={isRTL ? 'rtl' : 'ltr'}
      noValidate
    >
      <div>
        <Label htmlFor="fullNameAr">{t('initiative.coaches.form.fullNameAr')}</Label>
        <Input id="fullNameAr" className="mt-2" dir="rtl" {...register('fullNameAr')} />
        {errors.fullNameAr && (
          <p className="mt-1 text-xs text-destructive">{errors.fullNameAr.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="fullNameEn">{t('initiative.coaches.form.fullNameEn')}</Label>
        <Input id="fullNameEn" className="mt-2" dir="ltr" {...register('fullNameEn')} />
        {errors.fullNameEn && (
          <p className="mt-1 text-xs text-destructive">{errors.fullNameEn.message}</p>
        )}
      </div>

      <div className="md:col-span-2">
        <Label htmlFor="nationalId">{t('initiative.coaches.form.nationalId')}</Label>
        <Input
          id="nationalId"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          dir="ltr"
          maxLength={9}
          pattern="\d{9}"
          aria-required="true"
          aria-invalid={errors.nationalId ? 'true' : 'false'}
          aria-describedby="nationalId-hint nationalId-error"
          placeholder={t('initiative.coaches.form.nationalIdPlaceholder')}
          className={cn(
            'mt-2 font-mono tracking-wider',
            errors.nationalId && 'border-destructive focus-visible:ring-destructive'
          )}
          value={idValue}
          onChange={handleIdChange}
          onPaste={handleIdPaste}
          onKeyDown={handleIdKeyDown}
          onBlur={() => {
            if (idValue) setValue('nationalId', idValue, { shouldValidate: true });
          }}
        />
        <p id="nationalId-hint" className="mt-1 text-xs text-muted-foreground">
          {t('initiative.coaches.form.nationalIdHint')}
        </p>
        {errors.nationalId && (
          <p id="nationalId-error" className="mt-1 text-xs text-destructive">
            {errors.nationalId.message}
          </p>
        )}
      </div>

      {/* ─────── Credentials ─────── */}
      <div className="md:col-span-2">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <Label>{t('initiative.coaches.form.credentialsTitle')}</Label>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('initiative.coaches.form.credentialsHint')}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              appendCredential({ kind: 'license', title: '', issuer: '', reference: '' })
            }
            disabled={credentialFields.length >= 10}
          >
            <Plus className="h-3.5 w-3.5" />
            {t('initiative.coaches.form.addCredential')}
          </Button>
        </div>

        <div className="mt-3 space-y-3">
          {credentialFields.map((field, i) => {
            const kindValue = watch(`credentials.${i}.kind`) ?? 'license';
            const fieldErrors = errors.credentials?.[i];
            return (
              <div
                key={field.id}
                className="relative grid grid-cols-1 sm:grid-cols-12 gap-3 p-4 rounded-xl border border-border/60 bg-surface-1/40"
              >
                <div className="sm:col-span-3">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t('initiative.coaches.form.credentialType')}
                  </Label>
                  <Select
                    value={kindValue}
                    onValueChange={(v) =>
                      setValue(`credentials.${i}.kind`, v as CredentialKind, {
                        shouldValidate: true,
                      })
                    }
                  >
                    <SelectTrigger className="mt-1.5 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CREDENTIAL_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          <span className="inline-flex items-center gap-2">
                            <Award className="h-3.5 w-3.5 text-accent" />
                            {t(`initiative.coaches.form.credentialKinds.${k}`)}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-4">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t('initiative.coaches.form.credentialTitle')}
                  </Label>
                  <Input
                    className="mt-1.5"
                    placeholder={t('initiative.coaches.form.credentialTitlePlaceholder')}
                    {...register(`credentials.${i}.title`)}
                  />
                  {fieldErrors?.title && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.title.message}</p>
                  )}
                </div>

                <div className="sm:col-span-3">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t('initiative.coaches.form.credentialIssuer')}
                  </Label>
                  <Input
                    className="mt-1.5"
                    placeholder={t('initiative.coaches.form.credentialIssuerPlaceholder')}
                    {...register(`credentials.${i}.issuer`)}
                  />
                  {fieldErrors?.issuer && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.issuer.message}</p>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t('initiative.coaches.form.credentialReference')}
                  </Label>
                  <Input
                    className="mt-1.5"
                    placeholder={t('initiative.coaches.form.credentialReferencePlaceholder')}
                    {...register(`credentials.${i}.reference`)}
                  />
                </div>

                {credentialFields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeCredential(i)}
                    aria-label={t('initiative.coaches.form.removeFile')}
                    className={cn(
                      'absolute top-2 p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors',
                      isRTL ? 'left-2' : 'right-2'
                    )}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {errors.credentials && !Array.isArray(errors.credentials) && (
          <p className="mt-2 text-xs text-destructive">
            {(errors.credentials as { message?: string }).message}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="phone">{t('initiative.coaches.form.phone')}</Label>
        <Input id="phone" type="tel" className="mt-2" dir="ltr" placeholder="+970..." {...register('phone')} />
        {errors.phone && (
          <p className="mt-1 text-xs text-destructive">{errors.phone.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="email">{t('initiative.coaches.form.email')}</Label>
        <Input id="email" type="email" className="mt-2" dir="ltr" {...register('email')} />
        {errors.email && (
          <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="location">{t('initiative.coaches.form.location')}</Label>
        <Input id="location" className="mt-2" {...register('location')} />
        {errors.location && (
          <p className="mt-1 text-xs text-destructive">{errors.location.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="experience">{t('initiative.coaches.form.experience')}</Label>
        <Input
          id="experience"
          type="number"
          min={0}
          max={60}
          className="mt-2"
          dir="ltr"
          {...register('experience')}
        />
        {errors.experience && (
          <p className="mt-1 text-xs text-destructive">{errors.experience.message}</p>
        )}
      </div>

      {/* ─────── Unified Attachments Dropzone ─────── */}
      <div className="md:col-span-2">
        <Label>{t('initiative.coaches.form.attachmentsTitle')}</Label>
        <p className="mt-1 mb-3 text-xs text-muted-foreground">
          {t('initiative.coaches.form.attachmentsHint', { max: MAX_FILES })}
        </p>

        <input
          ref={fileInputRef}
          id="attachments"
          type="file"
          multiple
          onChange={handleFileInput}
          className="sr-only"
          aria-describedby="attachments-error"
        />

        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={cn(
            'group relative flex flex-col items-center justify-center gap-2 px-6 py-10 rounded-2xl border-2 border-dashed cursor-pointer transition-all',
            dragActive
              ? 'border-accent bg-accent/10 scale-[1.01]'
              : 'border-border/60 bg-surface-1/40 hover:border-accent/60 hover:bg-accent/5',
            attachError && 'border-destructive/60 bg-destructive/5'
          )}
        >
          <div
            className={cn(
              'h-12 w-12 rounded-2xl flex items-center justify-center transition-colors',
              dragActive
                ? 'bg-accent text-primary'
                : 'bg-accent/15 text-accent group-hover:bg-accent group-hover:text-primary'
            )}
          >
            <UploadCloud className="h-6 w-6" strokeWidth={1.8} />
          </div>
          <p className="text-sm font-semibold text-foreground">
            {dragActive
              ? t('initiative.coaches.form.dropzoneRelease')
              : t('initiative.coaches.form.dropzoneIdle')}
          </p>
          <p className="text-xs text-muted-foreground text-center max-w-md">
            {t('initiative.coaches.form.dropzoneSub')}
          </p>
          <div className="mt-1 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-card/80 border border-border/60 text-[11px] font-mono text-muted-foreground">
            {attachments.length}/{MAX_FILES} · max 10 MB / file
          </div>
        </div>

        {attachError && (
          <p id="attachments-error" className="mt-2 text-xs text-destructive">{attachError}</p>
        )}

        {attachments.length > 0 && (
          <ul className="mt-4 space-y-3">
            {attachments.map((a) => {
              const Icon = fileIcon(a.file);
              const isEditing = editingId === a.id;
              return (
                <li
                  key={a.id}
                  className="grid grid-cols-1 sm:grid-cols-[auto,1fr,auto,auto] items-center gap-3 px-3 py-2.5 rounded-xl border border-border/60 bg-surface-1/50"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Input
                            autoFocus
                            value={a.label}
                            onChange={(e) => updateAttachment(a.id, { label: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                setEditingId(null);
                              }
                            }}
                            onBlur={() => setEditingId(null)}
                            className="h-8 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="p-1 rounded hover:bg-accent/15 text-accent"
                            aria-label={t('initiative.coaches.form.saveLabel')}
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingId(a.id)}
                          className="group/label inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-accent transition-colors"
                          aria-label={t('initiative.coaches.form.editLabel')}
                        >
                          <span className="truncate max-w-[200px]">{a.label}</span>
                          <Pencil className="h-3.5 w-3.5 opacity-0 group-hover/label:opacity-100 transition-opacity" />
                        </button>
                      )}
                      <p className="text-[11px] text-muted-foreground truncate">
                        {a.file.name} · {formatBytes(a.file.size)}
                      </p>
                    </div>
                  </div>

                  <div />

                  <Select
                    value={a.kind}
                    onValueChange={(v) => updateAttachment(a.id, { kind: v as AttachmentKind })}
                  >
                    <SelectTrigger className="h-8 w-[140px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {KIND_OPTIONS.map((k) => (
                        <SelectItem key={k} value={k} className="text-xs">
                          {t(`initiative.coaches.form.kind.${k}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    aria-label={t('initiative.coaches.form.removeFile')}
                    className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="md:col-span-2">
        <Label htmlFor="licenseUrl">{t('initiative.coaches.form.licenseUrl')}</Label>
        <Input
          id="licenseUrl"
          type="url"
          className="mt-2"
          dir="ltr"
          placeholder="https://..."
          {...register('licenseUrl')}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          {t('initiative.coaches.form.licenseUrlHint')}
        </p>
      </div>

      {/* Preferred courses — two slots: primary + alternate (enrolled in one) */}
      <div className="md:col-span-2">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <Label className="inline-flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-accent" />
            {t('initiative.coaches.form.preferredCourses')}
          </Label>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {t('initiative.coaches.form.preferredCoursesHint')}
        </p>

        {/* Slot summary — primary + alternate */}
        <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
          {(['primary', 'alternate'] as const).map((slot) => {
            const id = slot === 'primary' ? selectedPrimaryId : selectedAlternateId;
            const c = id ? courses?.find((x) => x.id === id) : null;
            const title = c ? c.title[isRTL ? 'ar' : 'en'] || c.title.en : null;
            const partner = c ? c.partnerName[isRTL ? 'ar' : 'en'] || c.partnerName.en : null;
            return (
              <div
                key={slot}
                className={cn(
                  'rounded-xl border-2 p-3 flex items-start gap-3 transition-colors',
                  c
                    ? 'border-accent/60 bg-accent/5'
                    : 'border-dashed border-border/60 bg-surface-1/40'
                )}
              >
                <span
                  className={cn(
                    'flex items-center justify-center h-7 w-7 rounded-full font-display font-black text-xs flex-shrink-0',
                    c
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-surface-2 text-muted-foreground border border-border/60'
                  )}
                >
                  {slot === 'primary' ? '1' : '2'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-0.5">
                    {slot === 'primary'
                      ? t('initiative.coaches.form.preferredCoursePrimary')
                      : t('initiative.coaches.form.preferredCourseAlternate')}
                  </div>
                  {c && title ? (
                    <>
                      <div className="font-semibold text-sm text-foreground leading-snug line-clamp-2">
                        {title}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                        {partner}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">
                      {t('initiative.coaches.form.preferredCourseEmptySlot')}
                    </div>
                  )}
                </div>
                {c && (
                  <button
                    type="button"
                    onClick={() => clearSlot(slot)}
                    aria-label={t('initiative.coaches.form.preferredCourseClear')}
                    className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] font-editorial-italic text-foreground/65 leading-relaxed">
          {t('initiative.coaches.form.preferredCourseEnrollmentNote')}
        </p>

        {/* Search */}
        <div className="mt-3 relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            value={courseSearch}
            onChange={(e) => setCourseSearch(e.target.value)}
            placeholder={t('initiative.coaches.form.preferredCourseSearchPlaceholder')}
            className="ps-10"
            dir={isRTL ? 'rtl' : 'ltr'}
            aria-label={t('initiative.coaches.form.preferredCourseSearchPlaceholder')}
          />
          <div className="mt-1.5 text-[11px] text-muted-foreground">
            {coursesLoading
              ? t('initiative.coaches.form.preferredCoursesLoading')
              : t('initiative.coaches.form.preferredCourseResultCount', {
                  count: filteredCourses.length,
                })}
          </div>
        </div>

        {/* Course grid */}
        <div className="mt-3 rounded-xl border border-border/60 bg-surface-1/40 max-h-[34rem] overflow-y-auto">
          {coursesLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('initiative.coaches.form.preferredCoursesLoading')}
            </div>
          ) : coursesError ? (
            <div className="py-10 text-center text-sm text-destructive">
              {t('initiative.coaches.form.preferredCoursesError')}
            </div>
          ) : !courses || courses.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {t('initiative.coaches.form.preferredCoursesEmpty')}
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {t('initiative.coaches.form.preferredCourseNoMatch')}
            </div>
          ) : (
            <ul className="divide-y divide-border/40">
              {filteredCourses.map((c, idx) => {
                const slot = courseSlot(c.id);
                const isPicked = slot !== null;
                const title = c.title[isRTL ? 'ar' : 'en'] || c.title.en;
                const partner = c.partnerName[isRTL ? 'ar' : 'en'] || c.partnerName.en;
                const category = c.categoryName?.[isRTL ? 'ar' : 'en'];
                const dur = c.durationBilingual?.[isRTL ? 'ar' : 'en'] || c.duration;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setViewingCourseId(c.id)}
                      aria-pressed={isPicked}
                      aria-label={t('initiative.coaches.form.preferredCourseViewDetails', { title })}
                      className={cn(
                        'group w-full text-start px-3 sm:px-4 py-3 flex items-center gap-3 sm:gap-4 transition-colors',
                        isPicked
                          ? 'bg-accent/10 hover:bg-accent/15'
                          : 'hover:bg-surface-2/60'
                      )}
                    >
                      {/* Selection indicator — 1 / 2 / empty */}
                      <span
                        className={cn(
                          'flex items-center justify-center h-6 w-6 rounded-full border-2 font-display font-black text-[11px] flex-shrink-0 transition-colors',
                          slot === 'primary'
                            ? 'bg-accent border-accent text-accent-foreground'
                            : slot === 'alternate'
                              ? 'bg-accent/30 border-accent text-accent'
                              : 'border-foreground/30 group-hover:border-accent text-transparent'
                        )}
                        aria-hidden
                      >
                        {slot === 'primary' ? '1' : slot === 'alternate' ? '2' : ''}
                      </span>

                      {/* Index */}
                      <span className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground tabular-nums flex-shrink-0 w-7 text-end">
                        {String(idx + 1).padStart(2, '0')}
                      </span>

                      {/* Title + partner */}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm leading-snug text-foreground truncate">
                          {title}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                          {partner}
                          {category && (
                            <>
                              <span className="mx-1.5 opacity-50">·</span>
                              <span>{category}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Duration */}
                      <span className="hidden sm:inline-flex items-center px-2 py-1 rounded-md text-[10px] font-mono-mark uppercase tracking-widest text-foreground/70 bg-surface-2 border border-border/50 flex-shrink-0 whitespace-nowrap">
                        {dur}
                      </span>

                      {/* Action affordance */}
                      <span
                        className={cn(
                          'flex-shrink-0 text-[10px] font-mono-mark uppercase tracking-widest transition-colors',
                          isPicked ? 'text-accent' : 'text-muted-foreground group-hover:text-accent'
                        )}
                      >
                        {isRTL ? '←' : '→'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {errors.preferredCourseId && (
          <p className="mt-1 text-xs text-destructive">{errors.preferredCourseId.message}</p>
        )}
      </div>

      {/* Course details modal — opens on card click, lets user assign to primary or alternate slot */}
      <CourseDetailModal
        course={courses?.find((c) => c.id === viewingCourseId) ?? null}
        currentSlot={viewingCourseId ? courseSlot(viewingCourseId) : null}
        primaryFilled={!!selectedPrimaryId && selectedPrimaryId !== viewingCourseId}
        alternateFilled={
          !!selectedAlternateId && selectedAlternateId !== viewingCourseId
        }
        onClose={() => setViewingCourseId(null)}
        onAssign={(id, slot) => assignSlot(id, slot)}
      />

      <div className="md:col-span-2">
        <Label htmlFor="motivation">{t('initiative.coaches.form.motivation')}</Label>
        <Textarea
          id="motivation"
          rows={5}
          maxLength={500}
          placeholder={t('initiative.coaches.form.motivationPlaceholder')}
          className="mt-2"
          {...register('motivation')}
        />
        {errors.motivation && (
          <p className="mt-1 text-xs text-destructive">{errors.motivation.message}</p>
        )}
      </div>

      <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
        <p className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <Mail className="h-3.5 w-3.5" />
          {APPLICATIONS_EMAIL}
        </p>
        <Button type="submit" size="lg" className="btn-glow" disabled={submitting}>
          {submitting ? t('initiative.coaches.form.submitting') : t('initiative.coaches.form.submit')}
        </Button>
      </div>
    </motion.form>
  );
}
